import { inject, signal, OnInit, Directive } from '@angular/core';
import { FormGroup, FormArray } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { Location } from '@angular/common';
import { ContactApiService } from '@services/contacts/contact/contact-api.service';
import { Contact, IContact } from '@models/contacts/hrm/contacts';
import { AlertService, StaticDataService, statesIndia } from '@mavles/shared';
import { combineLatest } from 'rxjs';
import { ContactService } from '@services/contacts/contact.service';

@Directive()
export abstract class AbstractCreateContact implements OnInit {
  protected contactApiService = inject(ContactApiService);
  protected contactService = inject(ContactService);
  protected staticData = inject(StaticDataService);
  protected route = inject(ActivatedRoute);
  protected location = inject(Location);
  protected alertService = inject(AlertService);

  contactForm!: FormGroup;
  contactId: number | null = null;
  isEdit = false;
  isLoading = signal(false);
  isSaving = signal(false);
  error = signal('');
  statesIN = statesIndia;

  ngOnInit(): void {
    // 1. Build initial empty contact form
    this.contactForm = this.contactService.buildContactForm(new Contact());
    this.contactForm.get('contact_type_id')?.disable();

    // 2. Subscribe to query/route params to load/configure the contact
    combineLatest([
      this.route.queryParamMap,
      this.route.params,
    ]).subscribe(([queryParams, params]) => {
      // 3. Resolve edit vs create mode
      const idParam = params['id'] || queryParams.get('id');
      this.contactId = idParam ? Number(idParam) : null;

      if (this.contactId) {
        this.isEdit = true;
        this.loadContact();
      } else {
        this.isEdit = false;
        this.contactForm.get('contact_type_id')?.disable();
      }
    });

    // 4. Resolve and patch contact type once static configuration loads
    this.staticData.subscribeDataConfig().subscribe((data) => {
      if (data && !this.isEdit) {
        const typeCd = this.getInitialContactTypeCode();
        if (typeCd) {
          const cType = this.staticData.getContactTypeByCd(typeCd);
          if (cType && !this.contactForm.get('contact_type_id')?.value) {
            this.contactForm.patchValue({
              contact_type_id: cType.contact_type_id,
              contact_type_name: cType.contact_type_name
            });
          }
        }
      }
    });
  }

  loadContact() {
    if (!this.contactId) return;
    this.isLoading.set(true);
    this.contactApiService.get(this.contactId).subscribe({
      next: (res) => {
        if (res.success && res.data) {
          this.contactForm = this.contactService.buildContactForm(res.data);
          this.contactForm.get('contact_type_id')?.disable(); // disable in edit mode
          
          if (res.data.address?.length) {
            this.contactService.populateAddresses(this.contactForm, res.data.address);
          }
        } else {
          this.alertService.error('Error while retrieving data');
        }
        this.isLoading.set(false);
      },
      error: (err) => {
        this.isLoading.set(false);
        this.alertService.error('Error loading contact details');
        console.error('Error loading contact:', err);
      },
    });
  }

  // ─── ADDRESS HELPERS ────────────────────────────────────

  get address(): FormArray {
    return this.contactService.getAddressArray(this.contactForm);
  }

  addAddress() {
    this.triggerHaptic('light');
    this.contactService.addAddress(this.contactForm);
  }

  onAddressRemove(i: number) {
    this.triggerHaptic('light');
    this.contactService.onAddressRemove(this.contactForm, i);
  }

  getaddressClass(i: number): string {
    return this.contactService.getAddressClass(this.contactForm, i);
  }

  // ─── SUBMIT / SAVE ──────────────────────────────────────

  save() {
    if (this.contactForm.invalid) {
      this.contactForm.markAllAsTouched();
      const errors = this.contactService.getValidationErrors(this.contactForm);
      if (errors.length > 0) {
        errors.forEach((msg) => this.alertService.error(msg));
      } else {
        this.alertService.error('Please fill all required fields correctly.');
      }
      return;
    }

    this.isSaving.set(true);
    this.error.set('');

    const rawValue = this.contactForm.getRawValue();
    const cType = this.staticData.getContactTypeByID(rawValue.contact_type_id);

    const payload: IContact = {
      ...rawValue,
      contact_type_code: cType?.contact_type_code,
      contact_type_name: cType?.contact_type_name
    };
    if (this.isEdit && this.contactId) {
      payload.contact_id = this.contactId;
    }

    this.contactApiService.save(payload).subscribe({
      next: (res) => {
        this.isSaving.set(false);
        if (res.success) {
          this.contactForm.markAsPristine();
          this.triggerHaptic('medium');
          this.alertService.success(this.isEdit ? 'Contact updated successfully' : 'Contact created successfully');
          this.contactApiService.refresh();
          const savedId = (res.data?.contact_id || this.contactId) as number;
          this.onSaveSuccess(savedId, res.data);
        } else {
          this.triggerHaptic('heavy');
          this.error.set(res.message || 'Save failed');
          this.alertService.error(res.message || 'Save failed');
        }
      },
      error: (err) => {
        this.isSaving.set(false);
        this.triggerHaptic('heavy');
        this.alertService.handleError(err);
        this.error.set(err.error?.message || 'Save failed');
      },
    });
  }

  get isGroup() {
    if (this.contactForm?.get('contact_type_id')?.value) {
      return this.staticData.isContactTypeGroup(
        this.contactForm.get('contact_type_id')?.value
      );
    }
    return false;
  }

  // ─── ABSTRACT HOOKS ────────────────────────────────────

  protected abstract getInitialContactTypeCode(): string | null;

  protected abstract onSaveSuccess(contactId: number, data: any): void;

  protected triggerHaptic(type: 'light' | 'medium' | 'heavy'): void { console.log('triggerHaptic', type); }
}
